// =============================================================================
// wave-list.js
// Scroll-driven wave effect for .project-list[data-wave-list]
// Distance au centre viewport par item · lerp RAF · Lenis smooth scroll
// =============================================================================

import Lenis from 'lenis';

let rafId          = null;
let lenis          = null;
let resizeHandlers = [];
let mouseHandlers  = [];

// ── Config (lue depuis les CSS custom props sur :root) ───────────────────────

function readCSSVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function readCSSVarPx(name) {
  const raw = readCSSVar(name);
  const num = parseFloat(raw);
  if (raw.endsWith('vh'))  return num / 100 * window.innerHeight;
  if (raw.endsWith('vw'))  return num / 100 * window.innerWidth;
  if (raw.endsWith('rem')) return num * parseFloat(getComputedStyle(document.documentElement).fontSize);
  return num;
}


// ── Init ─────────────────────────────────────────────────────────────────────

export function init() {
  const list = document.querySelector('[data-wave-list]');
  if (!list) return;

  const mediaLayer = list.querySelector('.project-list__media-layer');
  if (!mediaLayer) return;

  const items = Array.from(list.querySelectorAll('[data-wave-item]'));
  if (!items.length) return;

  const LS_MAX       = parseFloat(readCSSVar('--wl-ls-max'));
  const LS_MIN       = parseFloat(readCSSVar('--wl-ls-min'));
  const OP_MIN       = parseFloat(readCSSVar('--wl-op-min'));
  const TITLE_OP_MIN = 0.3;
  let   RADIUS = readCSSVarPx('--wl-radius');

  const onResizeRadius = () => { RADIUS = readCSSVarPx('--wl-radius'); };
  window.addEventListener('resize', onResizeRadius, { passive: true });
  resizeHandlers.push(onResizeRadius);

  // ── Cache DOM queries (fix 1) ─────────────────────────────────────────────
  const titles      = items.map(i => i.querySelector('.project-itemlist__title'));
  const metas       = items.map(i => i.querySelector('.project-itemlist__meta'));
  const colsPerItem = items.map(i => Array.from(i.querySelectorAll('[data-meta-col]')));
  const mediaWraps  = Array.from(mediaLayer.querySelectorAll('.project-itemlist__media'));

  // Espace disponible mesuré depuis le DOM (positions naturelles sans transform), par item
  let maxSpreads = [];
  function calcMaxSpread() {
    colsPerItem.forEach(cols => {
      if (cols[0]) cols[0].style.transform = '';
      if (cols[1]) cols[1].style.transform = '';
    });
    maxSpreads = items.map((item, i) => {
      const ar   = item.getBoundingClientRect();
      const cols = colsPerItem[i];
      return {
        left:  cols[0] ? cols[0].getBoundingClientRect().left - ar.left : 0,
        right: cols[1] ? ar.right - cols[1].getBoundingClientRect().right : 0,
      };
    });
  }
  calcMaxSpread();
  const onResizeSpread = () => calcMaxSpread();
  window.addEventListener('resize', onResizeSpread, { passive: true });
  resizeHandlers.push(onResizeSpread);

  // État lerp par item
  const state = items.map(() => ({
    ls: LS_MAX, op: OP_MIN,
    s1: 0, s2: 0, s3: 0, s4: 0,
    sc: 0, mbl: 48,
    titleOp: 0.5, titleSc: 0.5,
    tLS: LS_MAX, tOP: OP_MIN,
    tS1: 0, tS2: 0, tS3: 0, tS4: 0,
    tSC: 0, tMbl: 48,
    tTitleOp: 0.5, tTitleSc: 0.5,
  }));

  // ── Point focal dynamique (scroll + souris) ──────────────────────────────
  let mouseY          = window.innerHeight / 2;
  let isPointerInList = false;
  let focalY          = window.innerHeight / 2;

  const onMouseMove  = (e) => { mouseY = e.clientY; };
  const onMouseEnter = ()  => { isPointerInList = true; };
  const onMouseLeave = ()  => { isPointerInList = false; };

  list.addEventListener('mousemove',  onMouseMove);
  list.addEventListener('mouseenter', onMouseEnter);
  list.addEventListener('mouseleave', onMouseLeave);

  // Quel item est directement sous la souris (pour forcer blur=0 sur l'item actif)
  const hoveredItem = { index: -1 };
  items.forEach((item, i) => {
    const onItemEnter = () => { hoveredItem.index = i; };
    const onItemLeave = () => { if (hoveredItem.index === i) hoveredItem.index = -1; };
    item.addEventListener('mouseenter', onItemEnter);
    item.addEventListener('mouseleave', onItemLeave);
    mouseHandlers.push(
      { el: item, type: 'mouseenter', fn: onItemEnter },
      { el: item, type: 'mouseleave', fn: onItemLeave },
    );
  });

  mouseHandlers = [
    { el: list, type: 'mousemove',  fn: onMouseMove },
    { el: list, type: 'mouseenter', fn: onMouseEnter },
    { el: list, type: 'mouseleave', fn: onMouseLeave },
    ...mouseHandlers,
  ];

  const LERP = 0.10;
  // LERP asymétrique pour mbl : rapide vers 0 (reveal), lent vers 48 (re-blur)
  const LERP_MBL_IN  = 0.14;
  const LERP_MBL_OUT = 0.04;
  const EPS  = 0.0005;
  function lerp(a, b, t) { return a + (b - a) * t; }
  function smoothstep(t) { return t * t * (3 - 2 * t); }

  // ── RAF loop ───────────────────────────────────────────────────────────────

  function tick() {
    const targetFocal = isPointerInList ? mouseY : window.innerHeight / 2;
    focalY = lerp(focalY, targetFocal, 0.06);

    // Passe 1 : tous les reads d'un coup — évite le layout thrashing (fix 2)
    const rects          = items.map(item => item.getBoundingClientRect());
    const mediaLayerRect = mediaLayer.getBoundingClientRect();

    // Passe 2 : calculs + writes
    items.forEach((item, i) => {
      const rect = rects[i];
      const cy   = rect.top + rect.height / 2;
      const dist = Math.abs(cy - focalY);
      const t    = smoothstep(Math.max(0, 1 - dist / RADIUS));
      const s    = state[i];

      const tMedia = t * t * t;           // cubic — media arrive uniquement vers la fin

      s.tLS      = lerp(LS_MAX, LS_MIN, t);
      s.tOP      = lerp(OP_MIN, 1,      t);
      s.tTitleOp = lerp(TITLE_OP_MIN, 1, t);
      s.tTitleSc = lerp(0.5,   1,      t);
      // Si la souris est directement sur cet item, forcer image nette (blur=0, scale=1)
      const isHovered = hoveredItem.index === i;
      s.tSC  = isHovered ? 1      : tMedia;
      s.tMbl = isHovered ? 0      : lerp(48, 0, tMedia);

      const spread = (1 - t);
      const ms = maxSpreads[i] ?? { left: 0, right: 0 };

      s.tS1 = -spread * ms.left;
      s.tS2 =  spread * ms.right;

      s.ls      = lerp(s.ls,      s.tLS,      LERP);
      s.op      = lerp(s.op,      s.tOP,      LERP);
      s.sc      = lerp(s.sc,      s.tSC,      LERP);
      s.mbl     = lerp(s.mbl,     s.tMbl,     s.tMbl < s.mbl ? LERP_MBL_IN : LERP_MBL_OUT);
      s.s1      = lerp(s.s1,      s.tS1,      LERP);
      s.s2      = lerp(s.s2,      s.tS2,      LERP);
      s.titleOp = lerp(s.titleOp, s.tTitleOp, LERP);
      s.titleSc = lerp(s.titleSc, s.tTitleSc, LERP);


      // Skip writes si toutes les valeurs ont convergé (fix 4)
      if (
        Math.abs(s.ls      - s.tLS)      < EPS &&
        Math.abs(s.op      - s.tOP)      < EPS &&
        Math.abs(s.sc      - s.tSC)      < EPS &&
        Math.abs(s.mbl     - s.tMbl)     < EPS &&
        Math.abs(s.titleOp - s.tTitleOp) < EPS &&
        Math.abs(s.titleSc - s.tTitleSc) < EPS
      ) return;

      const title = titles[i];
      if (title) {
        // title.style.letterSpacing = s.ls.toFixed(4) + 'em';
        title.style.opacity   = s.titleOp.toFixed(3);
        title.style.transform = `scale(${s.titleSc.toFixed(3)})`;
      }
      const meta = metas[i];
      if (meta) meta.style.opacity = s.titleOp.toFixed(3);
      const cols = colsPerItem[i];
      if (cols[0]) cols[0].style.transform = `translateX(${s.s1.toFixed(2)}px)`;
      if (cols[1]) cols[1].style.transform = `translateX(${s.s2.toFixed(2)}px)`;
      if (cols[2]) cols[2].style.transform = `translateX(${s.s3.toFixed(2)}px)`;
      if (cols[3]) cols[3].style.transform = `translateX(${s.s4.toFixed(2)}px)`;

      const mw = mediaWraps[i];
      if (mw) {
        const topOffset    = rect.top - mediaLayerRect.top + rect.height / 2;
        mw.style.top       = topOffset.toFixed(2) + 'px';
        mw.style.transform = `translate(-50%, -50%) scale(${s.sc.toFixed(3)})`;
        mw.style.filter    = `blur(${s.mbl.toFixed(2)}px)`;
        mw.style.opacity   = s.op.toFixed(3);
        mw.style.zIndex    = String(Math.round(t * 100));
      }
    });
  }

  // ── RAF unifié : Lenis + tick dans le même loop (fix 3) ───────────────────

  lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
  });

  function rafLoop(time) {
    lenis.raf(time);
    tick();
    rafId = requestAnimationFrame(rafLoop);
  }

  rafId = requestAnimationFrame(rafLoop);
}

// ── Destroy ───────────────────────────────────────────────────────────────────

export function destroy() {
  if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
  if (lenis) { lenis.destroy(); lenis = null; }
  resizeHandlers.forEach(fn => window.removeEventListener('resize', fn));
  resizeHandlers = [];
  mouseHandlers.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
  mouseHandlers = [];
}
